import { addDays, eachMinuteOfInterval, format, set } from "date-fns";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

export const MEETING_TIME_ZONE = "America/New_York";

type AvailabilityWindow = {
  weekday: number;
  start: number;
  end: number;
};

export type MeetingTimeParts = {
  hour: string;
  minute: string;
  ampm: "AM" | "PM";
};

const SLOT_INTERVAL_MINUTES = 15;

const AVAILABILITY_WINDOWS: AvailabilityWindow[] = [
  { weekday: 2, start: 18 * 60 + 30, end: 23 * 60 + 30 },
  { weekday: 4, start: 18 * 60 + 30, end: 23 * 60 + 30 },
  { weekday: 6, start: 18 * 60, end: 23 * 60 },
];

function getWindowForEasternDate(date: Date) {
  return AVAILABILITY_WINDOWS.find(
    (window) => window.weekday === date.getDay(),
  );
}

function getEasternDatesNearLocalDate(localDate: Date) {
  const dates = new Map<string, Date>();
  const localNoon = set(localDate, {
    hours: 12,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  for (let offset = -2; offset <= 2; offset += 1) {
    const easternDate = toZonedTime(
      addDays(localNoon, offset),
      MEETING_TIME_ZONE,
    );
    const key = format(easternDate, "yyyy-MM-dd");
    dates.set(
      key,
      set(easternDate, {
        hours: 12,
        minutes: 0,
        seconds: 0,
        milliseconds: 0,
      }),
    );
  }

  return Array.from(dates.values());
}

function getSlotsForEasternDate(easternDate: Date, now: Date) {
  const window = getWindowForEasternDate(easternDate);

  if (!window) {
    return [];
  }

  const start = set(easternDate, {
    hours: Math.floor(window.start / 60),
    minutes: window.start % 60,
    seconds: 0,
    milliseconds: 0,
  });
  const end = set(easternDate, {
    hours: Math.floor(window.end / 60),
    minutes: window.end % 60,
    seconds: 0,
    milliseconds: 0,
  });

  return eachMinuteOfInterval({ start, end }, { step: SLOT_INTERVAL_MINUTES })
    .map((easternSlot) => fromZonedTime(easternSlot, MEETING_TIME_ZONE))
    .filter((slot) => slot.getTime() >= now.getTime());
}

export function getMeetingTimeParts(date: Date): MeetingTimeParts {
  return {
    hour: format(date, "h"),
    minute: format(date, "mm"),
    ampm: format(date, "a") as "AM" | "PM",
  };
}

export function buildLocalDateTime(
  selectedDate: Date,
  selectedHour: string,
  selectedMinute: string,
  selectedAmPm: string,
) {
  let hour24 = parseInt(selectedHour, 10);

  if (selectedAmPm === "PM" && hour24 !== 12) {
    hour24 += 12;
  }
  if (selectedAmPm === "AM" && hour24 === 12) {
    hour24 = 0;
  }

  return set(selectedDate, {
    hours: hour24,
    minutes: parseInt(selectedMinute, 10),
    seconds: 0,
    milliseconds: 0,
  });
}

// Test accounts (#90) are not bound by the Eastern-time windows — QA has to be
// able to start a conversation a few minutes from now, which the fixed
// Tue/Thu/Sat evenings never allow. Every 15 minutes of the local day, minus
// the ones already past.
function getUnrestrictedSlotsForLocalDate(localDate: Date, now: Date) {
  const start = set(localDate, {
    hours: 0,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });
  const end = set(localDate, {
    hours: 23,
    minutes: 45,
    seconds: 0,
    milliseconds: 0,
  });

  return eachMinuteOfInterval(
    { start, end },
    { step: SLOT_INTERVAL_MINUTES },
  ).filter((slot) => slot.getTime() >= now.getTime());
}

export function getAvailableMeetingSlotsForLocalDate(
  localDate: Date,
  now = new Date(),
  options: { unrestricted?: boolean } = {},
) {
  const localDateKey = format(localDate, "yyyy-MM-dd");

  if (options.unrestricted) {
    return getUnrestrictedSlotsForLocalDate(localDate, now);
  }

  const slots = getEasternDatesNearLocalDate(localDate).flatMap((easternDate) =>
    getSlotsForEasternDate(easternDate, now),
  );

  return slots
    .filter((slot) => format(slot, "yyyy-MM-dd") === localDateKey)
    .sort((left, right) => left.getTime() - right.getTime());
}
